/*
 * Conversation export utilities.
 * Supports JSON, CSV, and Markdown formats.
 */

/**
 * Download a blob as a file.
 * @param {Blob} blob - Blob to download
 * @param {string} filename - Filename for download
 */
function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

/**
 * Export a conversation as JSON.
 * @param {Object} conv - Conversation object
 */
export function exportConversationJSON(conv) {
  const data = {
    version: 1,
    exportedAt: new Date().toISOString(),
    conversation: conv,
  };
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
  const filename = `sheet-ai-${conv.name.replace(/[^a-z0-9]/gi, "_")}-${Date.now()}.json`;
  downloadBlob(blob, filename);
}

/**
 * Export a conversation as CSV (messages as rows).
 * @param {Object} conv - Conversation object
 */
export function exportConversationCSV(conv) {
  const rows = [["timestamp", "role", "content"]];

  for (const msg of conv.messages) {
    if (msg.role === "tool") continue; // Skip tool results for CSV
    rows.push([
      conv.updatedAt || "",
      msg.role,
      (msg.content || "").replace(/"/g, '""').slice(0, 10000),
    ]);
  }

  const csv = rows.map((r) => r.map((c) => `"${c}"`).join(",")).join("\n");
  const blob = new Blob([csv], { type: "text/csv" });
  const filename = `sheet-ai-${conv.name.replace(/[^a-z0-9]/gi, "_")}-${Date.now()}.csv`;
  downloadBlob(blob, filename);
}

/**
 * Export a conversation as Markdown.
 * @param {Object} conv - Conversation object
 */
export function exportConversationMarkdown(conv) {
  let md = `# ${conv.name}\n\n`;
  md += `*Exported: ${conv.updatedAt}*\n\n---\n\n`;

  for (const msg of conv.messages) {
    if (msg.role === "user") {
      md += `## 👤 User\n\n${msg.content}\n\n`;
    } else if (msg.role === "assistant") {
      md += `## 🤖 Sheet AI\n\n${msg.content}\n\n`;
    }
  }

  const blob = new Blob([md], { type: "text/markdown" });
  const filename = `sheet-ai-${conv.name.replace(/[^a-z0-9]/gi, "_")}-${Date.now()}.md`;
  downloadBlob(blob, filename);
}

/**
 * Export all conversations as a single JSON file.
 * @param {Array} conversations - Array of conversation objects
 */
export function exportAllConversations(conversations) {
  const data = {
    version: 1,
    exportedAt: new Date().toISOString(),
    conversationCount: conversations.length,
    conversations,
  };
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
  const filename = `sheet-ai-all-conversations-${Date.now()}.json`;
  downloadBlob(blob, filename);
}
